import { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// Tailwind-restyled shell (replaces the Antd AppShell). Same routes/perms/behavior.
import { AppShellModern as AppShell } from './components/AppShellModern';
import { LoginModal, LoginRedirect } from './components/LoginModal';
import { RequireAuth } from './components/RequireAuth';
import { RequirePerm } from './components/RequirePerm';
import { RoleHome } from './components/RoleHome';
// Public marketing site (no auth) — landing / about / catalogue.
// Old public-site pages retired in the Phase 6 switch; the new premium site (imported below) owns
// the public routes now. The inquiry flow is kept — it's the live nearest-dealer business logic.
import { InquiryPage } from './site/InquiryPage';
import { InquiryStatusPage } from './site/InquiryStatusPage';
// NEW premium public marketing site (Phase 2 rebuild) — previewed at /site while it's built.
import { PublicLayout as NewSiteLayout } from './site/layouts/PublicLayout';
import NewHomePage from './site/pages/HomePage';
import NewProductsPage from './site/pages/ProductsPage';
import NewProductDetailsPage from './site/pages/ProductDetailsPage';
import NewCategoryPage from './site/pages/CategoryPage';
import NewCropSolutionsPage from './site/pages/CropSolutionsPage';
import NewCropDetailPage from './site/pages/CropDetailPage';
import NewAboutPage from './site/pages/AboutPage';
import NewContactPage from './site/pages/ContactPage';
import NewGalleryPage from './site/pages/GalleryPage';
import NewResourcesPage from './site/pages/ResourcesPage';
import NewFaqPage from './site/pages/FaqPage';
import NewLegalPage from './site/pages/LegalPage';
import NewRetailerEnquiryPage from './site/pages/RetailerEnquiryPage';
import NewDistributorEnquiryPage from './site/pages/DistributorEnquiryPage';
import NewSiteNotFound from './site/pages/NotFoundPage';
import { ExecSalesDashboard } from './pages/dashboards/ExecSalesDashboard';
import { SalesPerformancePage } from './pages/dashboards/SalesPerformancePage';
import { MapDashboard } from './pages/dashboards/MapDashboard';
import { InventoryDashboard } from './pages/dashboards/InventoryDashboard';
import { ComplaintDashboard } from './pages/dashboards/ComplaintDashboard';
import { PeoplePage } from './pages/people/PeoplePage';
import { RetailerDetailPage } from './pages/retailers/RetailerDetailPage';
import { ProductsListPage } from './pages/products/ProductsListPage';
import { WarehousesPage } from './pages/masters/WarehousesPage';
import { TerritoryPage } from './pages/territory/TerritoryPage';
import { TaxMastersPage } from './pages/masters/TaxMastersPage';
import { SchemesPage } from './pages/masters/SchemesPage';
import { CommercialTermsPage } from './pages/masters/CommercialTermsPage';
import { OrdersListPage } from './pages/orders/OrdersListPage';
import { NewOrderPage } from './pages/orders/NewOrderPage';
import { OrderDetailPage } from './pages/orders/OrderDetailPage';
import { InvoicesListPage } from './pages/billing/InvoicesListPage';
import { InvoiceDetailPage } from './pages/billing/InvoiceDetailPage';
import { ReturnsListPage } from './pages/returns/ReturnsListPage';
import { RETURNS_LIFECYCLE_ENABLED } from './lib/flags';
import { OutstandingAgingPage } from './pages/billing/OutstandingAgingPage';
import { PaymentsListPage } from './pages/billing/PaymentsListPage';
import { DiscountAdjustmentsPage } from './pages/billing/DiscountAdjustmentsPage';
import { CreditNotesPage } from './pages/billing/CreditNotesPage';
import { AttendancePage } from './pages/ffa/AttendancePage';
import { VisitsPage } from './pages/ffa/VisitsPage';
import { FeedbackPage } from './pages/ffa/FeedbackPage';
import { FieldJourneyPage } from './pages/ffa/FieldJourneyPage';
import { ComplaintsBoardPage } from './pages/complaints/ComplaintsBoardPage';
import { ComplaintDetailPage } from './pages/complaints/ComplaintDetailPage';
import { EventsListPage } from './pages/events/EventsListPage';
import { EventDetailPage } from './pages/events/EventDetailPage';
import { InquiriesPage } from './pages/inquiries/InquiriesPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { InventoryStockPage } from './pages/inventory/InventoryStockPage';
import { WarehouseActivityPage } from './pages/inventory/WarehouseActivityPage';
import { DispatchesPage } from './pages/inventory/DispatchesPage';
import { LowStockAlertsPage } from './pages/inventory/LowStockAlertsPage';
import { ShortfallsQueuePage } from './pages/orders/ShortfallsQueuePage';
import { MasterStockPage } from './pages/inventory/MasterStockPage';
import { BatchTracePage } from './pages/inventory/BatchTracePage';
import { GrnPage } from './pages/inventory/GrnPage';
import { OrderApprovalQueuePage } from './pages/orders/OrderApprovalQueuePage';
import { RolesPage } from './pages/admin/RolesPage';
import { BannersPage } from './pages/admin/BannersPage';
import { BannerEditorPage } from './pages/admin/BannerEditorPage';
import { BankDetailsPage } from './pages/admin/BankDetailsPage';
import { ActivityLogsPage } from './pages/admin/ActivityLogsPage';
import { AppInstallsPage } from './pages/admin/AppInstallsPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { NotificationPrefsPage } from './pages/profile/NotificationPrefsPage';
import { WalletsPage } from './pages/expense/WalletsPage';
import { ExpenseReportsPage } from './pages/expense/ExpenseReportsPage';
import { ExpenseReportDetailPage } from './pages/expense/ExpenseReportDetailPage';
import { MyAccountPage } from './pages/account/MyAccountPage';
import { NlAnalyticsPage } from './pages/ai/NlAnalyticsPage';
import { ForecastsPage } from './pages/ai/ForecastsPage';
import { TemplatesPage } from './pages/notifications/TemplatesPage';
import { WaTemplatesPage } from './pages/notifications/WaTemplatesPage';

// Each route declares the permission codes that may view it (ANY of). Routes
// with no `perm` only require authentication (self-service / landing). The
// codes mirror the backend @RequirePermissions guards so the UI cannot show a
// page the API would reject. SUPER_ADMIN (wildcard) passes everything.
type RouteDef = { path: string; element: ReactNode; perm?: string[]; denyRoles?: string[] };

// Customer (external) roles. Some staff pages are gated on a permission these roles ALSO hold for their
// own scope (e.g. STOCK.VIEW for the catalog stock indicator, RETAILER.VIEW/FARMER.VIEW/PRODUCT.VIEW,
// OUTSTANDING.VIEW for My Account). `denyRoles: CUSTOMER_ROLES` shuts those staff pages to them so they
// can't be reached by direct URL — their own shared pages (My Orders/Invoices/Account) stay open.
const CUSTOMER_ROLES = ['RETAILER', 'FARMER'];

const ROUTES: RouteDef[] = [
  // '/' is now the PUBLIC landing page (see the public routes below). RoleHome —
  // the per-role redirect into the app — moves to '/home' and is the target of
  // the landing's "Dashboard" button for signed-in users.
  { path: '/home', element: <RoleHome /> },

  { path: '/dashboard', element: <ExecSalesDashboard />, perm: ['DASH.EXECUTIVE', 'DASH.SALES'] },
  { path: '/dashboard/sales', element: <Navigate to="/dashboard" replace /> },
  { path: '/dashboard/inventory', element: <InventoryDashboard />, perm: ['DASH.INVENTORY'] },
  { path: '/dashboard/complaint', element: <ComplaintDashboard />, perm: ['DASH.COMPLAINT'] },
  { path: '/dashboard/map', element: <MapDashboard />, perm: ['DASH.MAP'] },
  { path: '/performance', element: <SalesPerformancePage />, perm: ['DASH.SALES'] },

  { path: '/people', element: <PeoplePage />, perm: ['RETAILER.VIEW', 'FARMER.VIEW', 'USER.VIEW'], denyRoles: CUSTOMER_ROLES },
  { path: '/retailers', element: <Navigate to="/people?tab=retailers" replace /> },
  { path: '/retailers/:id', element: <RetailerDetailPage />, perm: ['RETAILER.VIEW'], denyRoles: CUSTOMER_ROLES },
  { path: '/farmers', element: <Navigate to="/people?tab=farmers" replace /> },

  { path: '/products', element: <ProductsListPage />, perm: ['PRODUCT.VIEW'], denyRoles: CUSTOMER_ROLES },
  { path: '/inventory/warehouses', element: <WarehousesPage />, perm: ['WAREHOUSE.VIEW'] },
  // Moved Warehouses from Masters → Inventory; keep the old path as a redirect.
  { path: '/masters/warehouses', element: <Navigate to="/inventory/warehouses" replace /> },
  { path: '/masters/territories', element: <TerritoryPage />, perm: ['TERRITORY.VIEW', 'TERRITORY.ASSIGN', 'TERRITORY.ASSIGN.HEAD', 'TERRITORY.COVERAGE'] },
  // Coverage is now the "Coverage" tab of the Territories page; keep the old path as a deep-link redirect.
  { path: '/masters/territory-coverage', element: <Navigate to="/masters/territories?tab=coverage" replace /> },
  { path: '/masters/tax', element: <TaxMastersPage />, perm: ['TAX_MASTER.VIEW'] },
  { path: '/masters/schemes', element: <SchemesPage />, perm: ['PRODUCT.PRICE.EDIT'] },
  { path: '/masters/commercial-terms', element: <CommercialTermsPage />, perm: ['COMMERCIAL.VIEW'] },

  { path: '/inventory/stock', element: <InventoryStockPage />, perm: ['STOCK.VIEW'], denyRoles: CUSTOMER_ROLES },
  // Stock-operations hub: Master Stock + Transfers + Adjustments tabs. Perm is the UNION of the
  // three features so a Warehouse Manager (transfer/adjust, no master) can still reach their tabs;
  // the Master Stock tab itself is gated inside the page by STOCK.MASTER.ADD.
  { path: '/inventory/master-stock', element: <MasterStockPage />, perm: ['STOCK.MASTER.ADD', 'STOCK.VIEW', 'STOCK.TRANSFER', 'STOCK.ADJUST.CREATE', 'STOCK.ADJUST.APPROVE'], denyRoles: CUSTOMER_ROLES },
  { path: '/inventory/batch-trace', element: <BatchTracePage />, perm: ['STOCK.VALUATION.VIEW'] },
  { path: '/inventory/history', element: <Navigate to="/inventory/stock?tab=history" replace /> },
  // Valuation is merged into the Stock page's first tab; keep the old path as a redirect.
  { path: '/inventory/valuation', element: <Navigate to="/inventory/stock" replace /> },
  { path: '/inventory/grn', element: <GrnPage />, perm: ['GRN.VIEW'] },
  // Warehouse activity feed (order events + stock movements), scoped to the WM's warehouse(s); admins see all.
  { path: '/warehouse/activity', element: <WarehouseActivityPage />, perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'] },
  { path: '/warehouse/dispatches', element: <DispatchesPage />, perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'] },
  // Non-sellable stock is now a tab of the Stock & Valuation page; keep the old path as a redirect.
  { path: '/inventory/non-sellable', element: <Navigate to="/inventory/stock?tab=non-sellable" replace /> },
  { path: '/inventory/low-stock', element: <LowStockAlertsPage />, perm: ['WAREHOUSE.VIEW.ASSIGNED', 'WAREHOUSE.VIEW'] },
  // Transfers & Adjustments are now tabs of the Master Stock hub — keep the old paths as redirects
  // so existing links/bookmarks land on the right tab.
  { path: '/inventory/transfers', element: <Navigate to="/inventory/master-stock?tab=transfers" replace />, perm: ['STOCK.VIEW', 'STOCK.TRANSFER'], denyRoles: CUSTOMER_ROLES },
  { path: '/inventory/adjustments', element: <Navigate to="/inventory/master-stock?tab=adjustments" replace />, perm: ['STOCK.VIEW', 'STOCK.ADJUST.CREATE', 'STOCK.ADJUST.APPROVE'], denyRoles: CUSTOMER_ROLES },

  { path: '/orders', element: <OrdersListPage />, perm: ['ORDER.VIEW'] },
  { path: '/orders/approval', element: <OrderApprovalQueuePage />, perm: ['ORDER.APPROVE'] },
  { path: '/orders/new', element: <NewOrderPage />, perm: ['ORDER.CREATE'] },
  // Fulfillment V2 · Phase 2.5 — persisted shortfall queue. Literal route MUST precede '/orders/:id'.
  { path: '/orders/shortfalls', element: <ShortfallsQueuePage />, perm: ['ORDER.DISPATCH.PLAN', 'ORDER.EDIT', 'WAREHOUSE.PACK.START', 'WAREHOUSE.PACK.COMPLETE'] },
  { path: '/orders/:id', element: <OrderDetailPage />, perm: ['ORDER.VIEW'] },

  { path: '/inquiries', element: <InquiriesPage />, perm: ['INQUIRY.VIEW'] },

  { path: '/billing/invoices', element: <InvoicesListPage />, perm: ['INVOICE.VIEW'] },
  { path: '/billing/invoices/:id', element: <InvoiceDetailPage />, perm: ['INVOICE.VIEW'] },
  { path: '/billing/payments', element: <PaymentsListPage />, perm: ['PAYMENT.VIEW'] },
  { path: '/billing/credit-notes', element: <CreditNotesPage />, perm: ['INVOICE.VIEW'], denyRoles: CUSTOMER_ROLES },
  { path: '/billing/aging', element: <OutstandingAgingPage />, perm: ['OUTSTANDING.VIEW'], denyRoles: CUSTOMER_ROLES },
  { path: '/billing/discount-adjustments', element: <DiscountAdjustmentsPage />, perm: ['DISCOUNT.ADJUST.CREATE', 'DISCOUNT.ADJUST.APPROVE'] },

  // Sales returns (lifecycle) — list + detail. RETURN.VIEW is held by retailer (own), SP/SH, WHM, Accounts, Admin.
  // Gated on the lifecycle flag so the routes only exist when the backend accepts the actions.
  ...(RETURNS_LIFECYCLE_ENABLED ? [
    { path: '/returns', element: <ReturnsListPage />, perm: ['RETURN.VIEW'] },
    // Deep-links (notification bell, activity-log "Open record") land on the list, which reads the :id and
    // opens the SAME right-side drawer as an in-app row click — one consistent presentation, no separate page.
    { path: '/returns/:id', element: <ReturnsListPage />, perm: ['RETURN.VIEW'] },
  ] : []),

  // Retailer self-service statement of account (same ledger view as staff, self-scoped).
  { path: '/account', element: <MyAccountPage />, perm: ['OUTSTANDING.VIEW'] },

  { path: '/wallets', element: <WalletsPage />, perm: ['EXPENSE.VIEW'] },
  { path: '/expense-reports', element: <ExpenseReportsPage />, perm: ['EXPENSE.VIEW', 'EXPENSE.CREATE'] },
  { path: '/expense-reports/:id', element: <ExpenseReportDetailPage />, perm: ['EXPENSE.VIEW', 'EXPENSE.CREATE'] },

  { path: '/profile', element: <ProfilePage /> },
  { path: '/profile/notifications', element: <NotificationPrefsPage /> },

  { path: '/ffa/my-day', element: <FieldJourneyPage />, perm: ['ATTENDANCE.SELF', 'VISIT.CREATE'] },
  { path: '/ffa/attendance', element: <AttendancePage />, perm: ['ATTENDANCE.VIEW'] },
  { path: '/ffa/visits', element: <VisitsPage />, perm: ['VISIT.VIEW'] },
  { path: '/ffa/feedback', element: <FeedbackPage />, perm: ['FEEDBACK.VIEW'] },

  { path: '/complaints', element: <ComplaintsBoardPage />, perm: ['COMPLAINT.VIEW'] },
  { path: '/complaints/:id', element: <ComplaintDetailPage />, perm: ['COMPLAINT.VIEW'] },

  { path: '/events', element: <EventsListPage />, perm: ['EVENT.VIEW'] },
  { path: '/events/:id', element: <EventDetailPage />, perm: ['EVENT.VIEW'] },

  { path: '/reports', element: <ReportsPage />, perm: ['REPORT.RUN'] },

  { path: '/notifications/templates', element: <TemplatesPage />, perm: ['NOTIF.TEMPLATE.ADMIN'] },
  { path: '/notifications/wa-templates', element: <WaTemplatesPage />, perm: ['NOTIF.TEMPLATE.ADMIN'] },

  { path: '/ai/nl', element: <NlAnalyticsPage />, perm: ['AI.NL_ANALYTICS'] },
  { path: '/ai/forecasts', element: <ForecastsPage />, perm: ['AI.FORECAST.SALES'] },

  { path: '/admin/users', element: <Navigate to="/people?tab=users" replace /> },
  { path: '/admin/roles', element: <RolesPage />, perm: ['ROLE.VIEW'] },
  { path: '/admin/banners', element: <BannersPage />, perm: ['BANNER.MANAGE'] },
  { path: '/admin/banners/new', element: <BannerEditorPage />, perm: ['BANNER.MANAGE'] },
  { path: '/admin/banners/:id', element: <BannerEditorPage />, perm: ['BANNER.MANAGE'] },
  { path: '/admin/bank-details', element: <BankDetailsPage />, perm: ['COMPANY.BANK.EDIT'] },
  // Warehouse-manager assignment is now the "Managers" tab of the Warehouses page; keep the old path as a redirect.
  { path: '/admin/warehouse-managers', element: <Navigate to="/inventory/warehouses?tab=managers" replace /> },
  { path: '/admin/activity', element: <ActivityLogsPage />, perm: ['ACTIVITY.VIEW'] },
  { path: '/admin/app-installs', element: <AppInstallsPage />, perm: ['APP_INSTALL.VIEW'] },
];

export default function App() {
  return (
    <>
    <Routes>
      {/* Login is a MODAL now (mounted below), not a page. Keep /login as a
          graceful redirect so old links/bookmarks still pop the dialog on home. */}
      <Route path="/login" element={<LoginRedirect />} />

      {/* Public marketing site (new premium redesign) — no auth required. Owns the public "brochure"
          paths; the authenticated dashboard keeps /products, /events, /inquiries, /orders, etc.
          (verified non-colliding — the public product list is /catalog, not /products). */}
      <Route element={<NewSiteLayout />}>
        <Route path="/" element={<NewHomePage />} />
        <Route path="/catalog" element={<NewProductsPage />} />
        <Route path="/catalog/:slug" element={<NewProductDetailsPage />} />
        <Route path="/product-categories/:slug" element={<NewCategoryPage />} />
        <Route path="/crop-solutions" element={<NewCropSolutionsPage />} />
        <Route path="/crop-solutions/:slug" element={<NewCropDetailPage />} />
        <Route path="/about" element={<NewAboutPage />} />
        <Route path="/contact" element={<NewContactPage />} />
        <Route path="/gallery" element={<NewGalleryPage />} />
        {/* Old event-detail URLs redirect to the new gallery (which has an in-page lightbox). */}
        <Route path="/gallery/:id" element={<Navigate to="/gallery" replace />} />
        <Route path="/resources" element={<NewResourcesPage />} />
        <Route path="/faq" element={<NewFaqPage />} />
        <Route path="/retailer-enquiry" element={<NewRetailerEnquiryPage />} />
        <Route path="/distributor-enquiry" element={<NewDistributorEnquiryPage />} />
        <Route path="/privacy-policy" element={<NewLegalPage doc="privacy" />} />
        <Route path="/terms" element={<NewLegalPage doc="terms" />} />
        <Route path="/disclaimer" element={<NewLegalPage doc="disclaimer" />} />

        {/* Real product-inquiry flow (nearest dealer + status token) — kept as-is, reached from the
            product pages' "Enquire" button. This is the live inquiry business logic. */}
        <Route path="/inquiry" element={<Navigate to="/catalog" replace />} />
        <Route path="/inquiry/status/:token" element={<InquiryStatusPage />} />
        <Route path="/inquiry/:groupKey" element={<InquiryPage />} />
      </Route>

      {/* Authenticated back-office app. */}
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        {ROUTES.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={<RequirePerm anyOf={r.perm} denyRoles={r.denyRoles}>{r.element}</RequirePerm>}
          />
        ))}
      </Route>
      {/* Any unmatched path → the public 404 with the marketing chrome (nav + footer). */}
      <Route element={<NewSiteLayout />}>
        <Route path="*" element={<NewSiteNotFound />} />
      </Route>
    </Routes>
    {/* Global login modal — opened from the public header, the /login redirect,
        or any auth guard catching an unauthenticated visitor. */}
    <LoginModal />
    </>
  );
}
